import { useEffect, useRef } from "react";
import {
    useClearWorkflowState,
    useClearMediaState,
    useClearAllPredictions,
    useClearAllAnnotations,
    useClearInferenceQueue,
    useClearImproveState,
    useClearAllBoxPromptCache,
    useClearRapidDataState,
    useClearModelState,
    useSetPromptLabels
} from "@/store";

/**
 * Hook that resets all build session state when the user starts fresh.
 * Should be called on the homepage/upload page to ensure a clean slate
 * when building a new model.
 *
 * This prevents issues like:
 * - Old workflow from previous session being used for "Improve" navigation
 * - Old predictions/annotations carrying over to new builds
 * - Old prompt labels (classes/objects) appearing in new builds
 * - Stale inference queue items affecting new builds
 * - Model page media (test images, comparison sources) persisting across sessions
 */
export function useResetBuildSession() {
    const clearWorkflowState = useClearWorkflowState();
    const clearMediaState = useClearMediaState();
    const clearAllPredictions = useClearAllPredictions();
    const clearAllAnnotations = useClearAllAnnotations();
    const clearInferenceQueue = useClearInferenceQueue();
    const clearImproveState = useClearImproveState();
    const clearAllBoxPromptCache = useClearAllBoxPromptCache();
    const clearRapidDataState = useClearRapidDataState();
    const clearModelState = useClearModelState();
    const setPromptLabels = useSetPromptLabels();

    // Use ref to ensure we only reset once per mount
    const hasResetRef = useRef(false);

    useEffect(() => {
        if (hasResetRef.current) return;
        hasResetRef.current = true;

        // Clear all build session state
        clearWorkflowState();
        clearMediaState();
        clearAllPredictions();
        clearAllAnnotations();
        clearInferenceQueue();
        clearImproveState();
        clearAllBoxPromptCache();
        clearRapidDataState();
        clearModelState();
        setPromptLabels(new Map()); // Clear prompt labels (classes/objects)
    }, [
        clearWorkflowState,
        clearMediaState,
        clearAllPredictions,
        clearAllAnnotations,
        clearInferenceQueue,
        clearImproveState,
        clearAllBoxPromptCache,
        clearRapidDataState,
        clearModelState,
        setPromptLabels
    ]);
}
